<template>
  <Tabs
    v-if="overflowMode !== 'dropdown'"
    :value="value"
    :scrollable="true"
    :class="$style.sbTabs"
    data-sb-regression="sb-tabs-list"
  >
    <TabList ref="tabListContainer">
      <template
        v-for="tab in tabs"
        :key="tab.id"
      >
        <template v-if="$slots['tab-template']">
          <slot
            name="tab-template"
            :tab="tab"
            version="next"
          />
        </template>
        <SbTabNext
          v-else
          :disabled="tab.disabled"
          :delimit="tab.delimit"
          :tab="tab.id"
        >
          <sb-text
            :data-sb-automation="tab.name"
            :value="tab.name"
          />
        </SbTabNext>
      </template>
    </TabList>
  </Tabs>
  <SbTabsListNextDropdownOverflow
    v-else
    v-bind="($attrs, $props)"
    :active-tab="state.sbTabs.activeTab"
    :on-active-tab="setActiveTab"
  >
    <template
      v-if="$slots['tab-template']"
      #tab-template="slotProps"
    >
      <slot
        name="tab-template"
        v-bind="slotProps"
      />
    </template>
  </SbTabsListNextDropdownOverflow>
</template>

<script setup>
/* eslint-disable css-modules/no-unused-class */
import { provide, reactive, defineEmits, defineProps, watch, onMounted, ref, onUnmounted, defineOptions } from 'vue';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import SbText from '@src/components/Text/SbText.vue';
import $style from '@src/components/Tabs/style/sb-tabs.module.scss';
import SbTabNext from '@src/components/Tabs/components/SbTabNext.vue';
import { checkNextButtonVisibility, moveToFocusSelectedTab } from '@src/components/Tabs/utils/primevue-utils';
import SbTabsListNextDropdownOverflow from '@src/components/Tabs/SbTabsListNextDropdownOverflow.vue';

defineOptions({
  name: 'SbTabsListNext',
});

const tabListContainer = ref(null);

const props = defineProps({
  /**
   * List of tabs objects to be displayed.
   * Each tab requires an `id` and `name` attribute
   */
  tabs: {
    type: Array,
    default: () => [],
  },
  /**
   * Used to bind the active SbTab. Allows you to use the `v-model` syntax.
   */
  value: {
    default: null,
    type: [String, Number],
  },
  /**
   * Defines the overflow mode of the tabs.
   * Can be either 'dropdown' or 'scrollable'.
   */
  overflowMode: {
    type: String,
    default: 'scrollable',
    validator: (value) => ['dropdown', 'scrollable'].includes(value),
  },
  /**
   * Text of the overflow button.
   */
  overflowButtonText: {
    type: String,
    default: 'common.more',
  },
});

const emit = defineEmits(['update:value']);

const state = reactive({
  sbTabs: {
    activeTab: props.value,
  },
});

function setActiveTab(tab) {
  emit('update:value', tab);
  state.sbTabs.activeTab = tab;
  setTimeout(() => moveToFocusSelectedTab(tabListContainer.value?.$el));
}

watch(() => props.value, (newValue) => {
  state.sbTabs.activeTab = newValue;
  setTimeout(() => moveToFocusSelectedTab(tabListContainer.value?.$el));
});
watch(() => props.tabs, () => {
  setTimeout(() => checkNextButtonVisibility(tabListContainer.value?.$el));
});
provide('sbTabs', state.sbTabs);
provide('setActiveTab', setActiveTab);

const onScrollRef = ref(() => { checkNextButtonVisibility(tabListContainer.value?.$el); });
const resizeObserver = ref(null);

function getScrollableContainer() {
  return tabListContainer.value?.$el?.querySelector('[data-pc-section="content"]');
}

onMounted(() => {
  const tablistContent = getScrollableContainer();
  if (tablistContent) {
    tablistContent.addEventListener('scroll', onScrollRef.value);
    resizeObserver.value = new ResizeObserver(() => {
      checkNextButtonVisibility(tabListContainer.value.$el);
    });
    resizeObserver.value.observe(tablistContent);
  }
});
onUnmounted(() => {
  const tablistContent = getScrollableContainer();
  if (tablistContent) tablistContent.removeEventListener('scroll', onScrollRef.value);
  resizeObserver.value?.disconnect();
});

</script>
